/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { useEffect } from "react";

import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

import { useDetailsStore } from "@/stores/useDetailsStore";
import { useFiltersHeightStore } from "@/stores/useFiltersHeightStore";
import { useTableStoreByRoute } from "@/stores/useTableStore";
import { useResizeObserver } from "@usefy/use-resize-observer";

import { ROUTES, useMediaQueries } from "../../../shared";
import useFilteredValues from "../hooks/useFilteredValues";
import { useStyles } from "../styles";
import { FILTER_KEYS } from "../types/";
import ClearAllButton from "./ClearAllButton";
import FilteredValueTag from "./FilteredValueTag";
import FiltersTitle from "./FiltersTitle";

const FilteredValues = () => {
  const { t } = useTranslation();
  const { fromMd } = useMediaQueries();
  const location = useLocation();
  const { ref, height = 1 } = useResizeObserver();

  const { setParams, initialLoading } = useTableStoreByRoute(
    location.pathname as ROUTES
  );

  const resetDetailsSection = useDetailsStore(
    state => state.resetDetailsSection
  );
  const setFiltersHeightValue = useFiltersHeightStore(state => state.setHeight);

  const isInitialLoading = initialLoading || resetDetailsSection;

  const { filteredValues, handleDeleteFilterValue, resetCurrentPage } =
    useFilteredValues();

  const { filteredValuesDiv, filtersTabsDiv } = useStyles({
    isLoading: isInitialLoading,
    fromMd: fromMd
  });

  const hasFilteredValues = filteredValues.length > 0;

  useEffect(() => {
    hasFilteredValues
      ? setFiltersHeightValue(height + 16)
      : setFiltersHeightValue(0);
  }, [height, setFiltersHeightValue, hasFilteredValues]);

  const handleClearAll = () => {
    setParams({
      filters: null
    });
    resetCurrentPage();
  };

  return (
    <>
      {hasFilteredValues && (
        <div
          css={css(filteredValuesDiv)}
          data-testid="filtered-values-block"
          ref={ref}
        >
          <FiltersTitle isInitialLoading={isInitialLoading} />
          <div css={css(filtersTabsDiv)}>
            {filteredValues.map((item: any) => {
              const dateRangeFilter =
                item.name === FILTER_KEYS.DateRangeFrom ||
                item.name === FILTER_KEYS.DateRangeTo;

              return (
                <FilteredValueTag
                  key={dateRangeFilter ? item.name : item.id}
                  isInitialLoading={isInitialLoading}
                  item={item}
                  onDelete={handleDeleteFilterValue}
                />
              );
            })}
            <ClearAllButton
              onButtonClick={handleClearAll}
              buttonText={t("Button##clear all")}
              isInitialLoading={isInitialLoading}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default FilteredValues;
